import React from 'react';
import { Box, Typography, Container, List, ListItem, ListItemIcon, ListItemText, Divider } from '@mui/material';

const sections = [
  {
    title: '1. Purpose',
    paragraphs: [
      'FILM-HOOK MEDIAAPPS PRIVATE LIMITED ("Filmhook Apps", "Filmhook", "Company", "we", "our", or "us") is committed to providing users with meaningful control over their accounts, personal information, privacy settings, and platform experience.',
      'This Manage Account Policy explains how users can manage, update, maintain, and secure their Filmhook Apps account throughout its lifecycle without permanently deleting it.',
      'This Policy should be read together with our:'
    ],
    bullets: [
      'Privacy Policy',
      'Data Safety & Security Policy',
      'Terms of Service',
      'Community Guidelines',
      'Account Deletion Policy'
    ]
  },
  {
    title: '2. Eligibility',
    paragraphs: [
      'Only registered account owners may manage their Filmhook Apps account.',
      'Certain account management features may require identity verification before changes can be applied.',
      'Users must be at least 18 years of age to maintain an individual account on Filmhook Apps.'
    ]
  },
  {
    title: '3. Account Management Features',
    paragraphs: [
      'Filmhook Apps allows eligible users to manage various aspects of their account, including:'
    ],
    bullets: [
      'Personal information',
      'Professional profile',
      'Business profile',
      'Portfolio',
      'Profile picture',
      'Cover photo',
      'Contact information',
      'Password',
      'Privacy settings',
      'Notification preferences',
      'Security settings',
      'Connected devices',
      'Language preferences',
      'Marketplace information',
      'Recruitment information',
      'Account visibility',
      'Account deletion'
    ],
    footerParagraph: 'Available features may change as the platform evolves.'
  },
  {
    title: '4. Updating Account Information',
    paragraphs: [
      'Users may update their account information at any time through the account settings. Information that may be updated includes:'
    ],
    bullets: [
      'Full name',
      'Display name',
      'Biography',
      'Contact details',
      'Professional information',
      'Skills',
      'Experience',
      'Company information',
      'Portfolio details',
      'Website links',
      'Social media links',
      'Profile photographs',
      'Cover images'
    ],
    footerParagraph: 'Users are responsible for ensuring that all submitted information remains accurate and up to date.'
  },
  {
    title: '5. Managing Your Profile',
    paragraphs: [
      'Filmhook Apps allows users to personalize their professional presence by managing:'
    ],
    bullets: [
      'Profile photographs',
      'Portfolio',
      'Career information',
      'Business details',
      'Professional achievements',
      'Awards',
      'Certifications',
      'Projects',
      'Services',
      'Marketplace listings',
      'Recruitment information'
    ],
    footerParagraph: 'Users remain solely responsible for all information displayed on their profiles.'
  },
  {
    title: '6. Password Management',
    paragraphs: [
      'Users are responsible for maintaining the confidentiality of their password.',
      'Users should:'
    ],
    bullets: [
      'Create strong passwords.',
      'Keep passwords confidential.',
      'Avoid sharing login credentials.',
      'Update passwords if compromise is suspected.',
      'Immediately report unauthorized account access.'
    ],
    footerParagraph: 'Filmhook Apps may require password verification before certain account changes.'
  },
  {
    title: '7. Privacy Controls',
    paragraphs: [
      'Users may control various privacy preferences, including:'
    ],
    bullets: [
      'Profile visibility',
      'Contact information visibility',
      'Professional profile visibility',
      'Business profile visibility',
      'Portfolio visibility',
      'Search visibility',
      'Notification settings'
    ],
    footerParagraph: 'Some information may remain visible where necessary for platform functionality or legal compliance.'
  },
  {
    title: '8. Notification Preferences',
    paragraphs: [
      'Users may customize notifications relating to:'
    ],
    bullets: [
      'Messages',
      'Marketplace activities',
      'Recruitment updates',
      'Audition notifications',
      'Connection requests',
      'Security alerts',
      'System announcements',
      'Platform updates'
    ],
    footerParagraph: 'Important security or legal notifications may continue to be delivered regardless of individual notification preferences.'
  },
  {
    title: '9. Managing Uploaded Content',
    paragraphs: [
      'Users may edit, replace, or remove their own uploaded content, including:'
    ],
    bullets: [
      'Photos',
      'Videos',
      'Portfolio items',
      'Marketplace listings',
      'Recruitment posts',
      'Business information',
      'Profile details'
    ],
    footerParagraph: 'Removal of content may not immediately affect backup systems or legal records retained under applicable law.'
  },
  {
    title: '10. Account Security',
    paragraphs: [
      'Users are encouraged to regularly review account activity and immediately report suspected unauthorized access.',
      'Users should:'
    ],
    bullets: [
      'Secure their device.',
      'Protect login credentials.',
      'Avoid phishing attempts.',
      'Use trusted networks.',
      'Log out from shared devices.'
    ],
    footerParagraph: 'Filmhook Apps may temporarily restrict account access if suspicious activity is detected.'
  },
  {
    title: '11. Account Restrictions',
    paragraphs: [
      'Filmhook Apps may temporarily limit certain account features where necessary to:'
    ],
    bullets: [
      'Protect platform security.',
      'Investigate suspected misuse.',
      'Comply with legal obligations.',
      'Enforce Terms of Service.',
      'Protect other users.'
    ],
    footerParagraph: 'Where appropriate, users may be informed of such restrictions.'
  },
  {
    title: '12. Deactivating or Deleting Your Account',
    paragraphs: [
      'Users who no longer wish to use Filmhook Apps may permanently delete their account by following the procedures described in the Account Deletion Policy.',
      'Permanent deletion results in loss of access to the account and associated services, subject to applicable legal and operational data retention requirements.'
    ]
  },
  {
    title: 'Contact Us',
    paragraphs: [
      'If you require assistance managing your account or experience difficulty accessing account settings, you may contact Filmhook Apps through our official support channels. For security purposes, we may request additional information to verify account ownership before processing sensitive account changes.'
    ]
  }
];

const ManageAccount = () => {
  return (
    <Box sx={{ py: { xs: 4, sm: 8, md: 12 }, minHeight: '80vh' }}>
      <Container maxWidth="md">
        <Box
          sx={{
            background: 'var(--bg-card)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid var(--border-color)',
            borderRadius: '24px',
            p: { xs: 2.5, sm: 4, md: 7 },
            boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
          }}
        >
          <Typography variant="h3" fontWeight={800} gutterBottom sx={{ color: 'var(--text-heading)', textAlign: 'center', mb: 1, fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' } }}>
            Manage Account Policy
          </Typography>
          <Typography variant="h6" sx={{ color: '#5dade2', textAlign: 'center', mb: 3, fontWeight: 500, letterSpacing: '0.5px', fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.25rem' } }}>
            FILM-HOOK MEDIAAPPS PRIVATE LIMITED
          </Typography>
          <Box sx={{ width: 80, height: 3, background: 'linear-gradient(90deg, #07a4f5, #5dade2)', mx: 'auto', mb: 6, borderRadius: '2px' }} />

          {sections.map((section, idx) => (
            <Box key={idx} sx={{ mb: 6 }}>
              <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: 'var(--text-heading)', mb: 2, fontSize: { xs: '1.2rem', sm: '1.35rem', md: '1.5rem' } }}>
                {section.title}
              </Typography>
              
              {section.paragraphs.map((p, pIdx) => (
                <Typography key={pIdx} variant="body1" sx={{ color: 'var(--text-secondary)', lineHeight: 1.8, mb: 2, textAlign: 'justify', fontSize: { xs: '0.88rem', md: '1rem' } }}>
                  {p}
                </Typography>
              ))}

              {section.bullets && (
                <List sx={{ pl: 2, mb: 2 }}>
                  {section.bullets.map((bullet, bIdx) => (
                    <ListItem key={bIdx} alignItems="flex-start" disableGutters sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: '32px', mt: '8px' }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#5dade2' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body1" sx={{ color: 'var(--text-secondary)', lineHeight: 1.7, textAlign: 'justify', fontSize: { xs: '0.88rem', md: '1rem' } }}>
                            {bullet}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}

              {section.footerParagraph && (
                <Typography variant="body1" sx={{ color: 'var(--text-secondary)', lineHeight: 1.8, mt: 2, textAlign: 'justify', fontSize: { xs: '0.88rem', md: '1rem' } }}>
                  {section.footerParagraph}
                </Typography>
              )}

              {idx < sections.length - 1 && (
                <Divider sx={{ mt: 5, borderColor: 'var(--border-subtle)' }} />
              )}
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default ManageAccount;
